import React, { createContext, useContext, useState, useEffect } from 'react';
import authService from '../services/authService';

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Auto-login on mount if session cookies exist
    useEffect(() => {
        const initAuth = async () => {
            try {
                // Call /auth/me directly — backend transparently refreshes
                // the access_token cookie if it's expired (using refresh_token cookie)
                const userData = await authService.getCurrentUser();
                if (userData) {
                    setUser(userData);
                }
            } catch (err) {
                // 401/403 = session not active — expected on public pages, not an error
            } finally {
                setLoading(false);
            }
        };

        initAuth();
    }, []);

    const login = async (email, password) => {
        try {
            setError(null);
            const { user: userData } = await authService.login(email, password);
            setUser(userData);
            return { success: true };
        } catch (err) {
            setError(err.message);
            return { success: false, error: err.message };
        }
    };

    const register = async (userData) => {
        try {
            setError(null);
            const response = await authService.register(userData);
            return { success: true, data: response };
        } catch (err) {
            setError(err.message);
            return { success: false, error: err.message };
        }
    };

    const logout = async () => {
        await authService.logout();
        setUser(null);
    };

    const updateUser = async () => {
        try {
            const userData = await authService.getCurrentUser();
            setUser(userData);
        } catch {
            // session may have expired — leave user state as-is
        }
    };

    const value = {
        user,
        loading,
        error,
        login,
        register,
        logout,
        updateUser,
        isAuthenticated: !!user,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
